import React, { useMemo, useState } from "react"
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'
import ReactApexChart from 'react-apexcharts'

import Rightbar from 'pages/main/cheques/Rightbar'
import { httpOk, GET } from 'helpers/api'
import { formatMoney, formatDate } from 'helpers/helpers'
import { StandardFields, FilterSegment } from 'pages/main/analytics/parts/AnalyticsFilterBar'
import useAnalyticsOptions from 'pages/main/analytics/parts/useAnalyticsOptions'
import { buildAnalyticsUrl } from 'pages/main/analytics/parts/analyticsDates'
import ChainFilterBar, { useChainFilters, ExcelButton } from '../parts/ChainFilterBar'
import { downloadExcel, periodText } from '../parts/chainExcel'
import {
	money, percent, columnStats, heatStyle, intensityStyle, chainColor,
	FillCell, useSort, SortTh, ChainKpi, HeatLegend, ChainEmpty,
} from '../parts/chainUi'

// Что показывает ячейка матрицы
const CELL_MODES = ['amount', 'percent', 'cheques']

// Группы способов оплаты — порядок строк и цвета графика
const GROUPS = ['cash', 'cashless', 'debt', 'other']
const GROUP_COLORS = { cash: '#12b886', cashless: '#5b73e8', debt: '#f06548', other: '#adb5bd' }

function Payments() {
	const { t } = useTranslation()
	const [data, setData] = useState(null)
	const [cellMode, setCellMode] = useState('amount')
	const [chartMode, setChartMode] = useState('amount')
	const [details, setDetails] = useState(null)
	const [showRightBar, setShowRightBar] = useState(false)
	const [selectedCheque, setSelectedCheque] = useState({})

	const filters = useChainFilters({ order_type_ids: [], source_ids: [] }, searchWithFilter)
	const { applied } = filters
	const options = useAnalyticsOptions(['sources'])

	async function searchWithFilter(filter) {
		const response = await GET(buildAnalyticsUrl('/chain/report/payments', filter), {}, { loader: false })
		if (httpOk(response)) setData(response.data)
	}

	const branches = useMemo(() => data?.branches || [], [data])
	const types = useMemo(() => data?.types || [], [data])
	const totals = data?.totals || {}
	const branchById = useMemo(() => Object.fromEntries(branches.map(item => [item.pos_id, item])), [branches])

	const groupLabels = {
		cash: t('chain_pay_group_cash'),
		cashless: t('chain_pay_group_cashless'),
		debt: t('chain_pay_group_debt'),
		other: t('chain_pay_group_other'),
	}
	const typeName = (type) => type.payment_type_id === 0 ? t('chain_payment_unknown') : type.name

	// Значение ячейки в выбранном режиме
	function cellValue(type, posId) {
		if (cellMode === 'cheques') return Number(type.counts?.[posId] || 0)
		const amount = Number(type.poses?.[posId] || 0)
		if (cellMode === 'amount') return amount
		const branch = branchById[posId]
		return branch?.amount > 0 ? amount / branch.amount * 100 : null
	}

	function formatCell(value) {
		if (value === null || value === undefined || value === 0) return '—'
		if (cellMode === 'amount') return money(value)
		if (cellMode === 'cheques') return formatMoney(value)
		return percent(value)
	}

	const readSortValue = (row, key) => {
		if (key.startsWith('pos:')) return cellValue(row, Number(key.slice(4)))
		if (key === 'name') return typeName(row)
		return row[key]
	}
	const { sorted, sort, toggle } = useSort(types, null, 'desc', readSortValue)
	// Без сортировки — по группам (наличные → безнал → долг → прочее), как прислал сервер
	const rows = sort.key ? sorted : types

	const groupStats = useMemo(() => ({
		cash: columnStats(branches, row => row.group_percent?.cash),
		cashless: columnStats(branches, row => row.group_percent?.cashless),
		debt: columnStats(branches, row => row.group_percent?.debt),
		other: columnStats(branches, row => row.group_percent?.other),
	}), [branches])
	const amountStats = useMemo(() => columnStats(branches, 'amount'), [branches])
	const hasReturns = Number(totals.returns || 0) > 0

	// Больше всего наличных — риск: деньги, которые не видит банк
	const cashLeader = useMemo(() => {
		const list = branches.filter(item => item.amount > 0 && item.group_percent?.cash !== null)
		return list.length > 1 ? [...list].sort((a, b) => b.group_percent.cash - a.group_percent.cash)[0] : null
	}, [branches])

	async function openDetails(posId, type, kind = 'payments') {
		const response = await GET(buildAnalyticsUrl('/chain/report/payments-details', applied, {
			pos_id: posId || null,
			payment_type_id: type ? type.payment_type_id : null,
			kind,
		}), {}, { loader: true })
		if (httpOk(response)) {
			setDetails({
				...response.data,
				kind,
				pos_name: posId ? branchById[posId]?.pos_name : t('chain_all_branches'),
				type_name: kind === 'returns' ? t('chain_returns') : (type ? typeName(type) : t('chain_all_payment_types')),
			})
		}
	}

	function closeDetails() {
		setDetails(null)
		setShowRightBar(false)
		setSelectedCheque({})
	}

	async function openCheque(chequeId) {
		if (!chequeId) return
		const response = await GET(`/cheques/${chequeId}`, {}, { loader: true })
		if (httpOk(response)) {
			setSelectedCheque(response.data.data)
			setShowRightBar(true)
		}
	}

	function orderTypeName(value) {
		if (Number(value) === 1) return t('hall')
		if (Number(value) === 2) return t('takeaway')
		if (Number(value) === 3) return t('delivery')
		return '—'
	}

	function exportExcel() {
		if (!data) return
		const period = `${t('period')}: ${periodText(data.start_date, data.end_date)}`
		const matrixColumns = (type) => [
			{ label: t('payment_type'), type: 'text', width: 26 },
			{ label: t('chain_pay_group'), type: 'text', width: 14 },
			...branches.map(branch => ({ label: branch.pos_name, type })),
			{ label: t('total'), type },
		]
		const matrixRows = (read) => types.map(type => [
			typeName(type), groupLabels[type.group],
			...branches.map(branch => read(type, branch.pos_id)),
			read(type, null),
		])

		const sheets = [
			{
				name: t('chain_mode_amount'),
				title: `${t('chain_payments_title')} — ${t('chain_mode_amount')}`,
				subtitle: period,
				columns: [...matrixColumns('money'), { label: t('share'), type: 'percent', width: 9 }],
				rows: types.map(type => [
					typeName(type), groupLabels[type.group],
					...branches.map(branch => Number(type.poses?.[branch.pos_id] || 0)),
					type.amount, type.share,
				]),
				footer: [t('chain_total_payments'), '', ...branches.map(branch => branch.amount), totals.amount, 100],
			},
		]
		if (hasReturns) {
			sheets[0].rows.push([t('chain_returns'), '', ...branches.map(branch => -branch.returns), -totals.returns, ''])
			sheets[0].rows.push([t('chain_net_revenue'), '', ...branches.map(branch => branch.net), totals.net, ''])
		}
		sheets.push({
			name: t('chain_pay_cheques_count'),
			title: `${t('chain_payments_title')} — ${t('chain_pay_cheques_count')}`,
			subtitle: period,
			columns: matrixColumns('int'),
			rows: matrixRows((type, posId) => posId === null ? type.cheques : Number(type.counts?.[posId] || 0)),
		})
		sheets.push({
			name: t('chain_branches'),
			title: `${t('chain_payments_title')} — ${t('chain_branches')}`,
			subtitle: period,
			columns: [
				{ label: t('chain_branch'), type: 'text', width: 28 },
				{ label: t('cheques'), type: 'int', width: 10 },
				{ label: t('chain_total_payments'), type: 'money' },
				{ label: t('chain_returns'), type: 'money' },
				{ label: t('chain_net_revenue'), type: 'money' },
				...GROUPS.flatMap(group => [
					{ label: groupLabels[group], type: 'money' },
					{ label: `${groupLabels[group]}, %`, type: 'percent', width: 10 },
				]),
				{ label: t('chain_overpaid'), type: 'money' },
			],
			rows: [...branches].sort((a, b) => b.amount - a.amount).map(branch => [
				branch.pos_name, branch.cheques, branch.amount, branch.returns, branch.net,
				...GROUPS.flatMap(group => [branch.by_group?.[group], branch.group_percent?.[group]]),
				branch.overpaid,
			]),
			footer: [
				t('total'), totals.cheques, totals.amount, totals.returns, totals.net,
				...GROUPS.flatMap(group => [totals.by_group?.[group], totals.group_percent?.[group]]),
				totals.overpaid,
			],
		})
		downloadExcel(`${t('chain_payments_title')} ${periodText(data.start_date, data.end_date)}`, sheets)
	}

	function exportDetails() {
		if (!details) return
		const isReturns = details.kind === 'returns'
		downloadExcel(`${details.type_name} — ${details.pos_name}`, [{
			name: isReturns ? t('chain_returns') : t('cheques'),
			title: `${details.type_name} — ${details.pos_name}`,
			subtitle: `${t('period')}: ${periodText(applied.start_date, applied.end_date)}`,
			columns: [
				{ label: t('date'), type: 'text', width: 18 },
				{ label: t('chain_branch'), type: 'text', width: 24 },
				{ label: t('chain_cheque_no'), type: 'text', width: 10 },
				{ label: t('cashier'), type: 'text', width: 18 },
				{ label: isReturns ? t('comment') : t('payment_types'), type: 'text', width: 24 },
				{ label: t('amount'), type: 'money' },
				{ label: isReturns ? t('payment_type') : t('chain_cheque_amount'), type: isReturns ? 'text' : 'money', width: 16 },
			],
			rows: details.rows.map(row => [
				formatDate(row.date, 'dd.MM.yyyy HH:mm'), row.pos_name, row.cheque_number, row.cashier_name || '',
				isReturns ? (row.comment || '') : (row.payment_types || ''),
				row.amount,
				isReturns ? row.payment_type_name : row.cheque_amount,
			]),
			footer: [t('total'), '', '', '', '', details.total, ''],
		}])
	}

	const modeLabels = {
		amount: t('chain_mode_amount'),
		percent: t('chain_pay_mode_percent'),
		cheques: t('chain_pay_cheques_count'),
	}

	return (
		<>
			<ChainFilterBar
				title={t('chain_payments_title')}
				icon="uil-credit-card"
				filters={filters}
				wide
				onExcel={exportExcel}
				excelDisabled={!data}
			>
				<StandardFields filters={filters} options={options} branch={false} />
			</ChainFilterBar>

			{Number(totals.overpaid || 0) > 0 && (
				<div className="chain-note mb-3">
					<i className="uil uil-info-circle"></i>
					<span>{t('chain_overpaid_note')} {money(totals.overpaid)}</span>
				</div>
			)}

			<div className="row g-3 mb-3">
				<div className="col-xl col-md-4 col-6">
					<ChainKpi title={t('chain_total_payments')} icon="uil-wallet" color="#5b73e8"
						value={money(totals.net)}
						sub={<>{formatMoney(totals.cheques || 0)} {t('chain_cheques_lower')}{hasReturns && <> · {t('chain_returns')} −{money(totals.returns)}</>}</>} />
				</div>
				<div className="col-xl col-md-4 col-6">
					<ChainKpi title={groupLabels.cash} icon="uil-money-bill" color={GROUP_COLORS.cash}
						value={percent(totals.group_percent?.cash)}
						sub={money(totals.by_group?.cash)} />
				</div>
				<div className="col-xl col-md-4 col-6">
					<ChainKpi title={groupLabels.cashless} icon="uil-credit-card" color={GROUP_COLORS.cashless}
						value={percent(totals.group_percent?.cashless)}
						sub={money(totals.by_group?.cashless)} />
				</div>
				<div className="col-xl col-md-6 col-6">
					<ChainKpi title={groupLabels.debt} icon="uil-file-contract-dollar" color={GROUP_COLORS.debt}
						value={money(totals.by_group?.debt)}
						title2={t('chain_pay_debt_hint')}
						sub={<>{percent(totals.group_percent?.debt)} {t('chain_of_payments')}</>} />
				</div>
				<div className="col-xl col-md-6 col-12">
					<ChainKpi title={t('chain_cash_leader')} icon="uil-exclamation-triangle" color="#f59f00"
						title2={t('chain_cash_leader_hint')}
						value={cashLeader ? cashLeader.pos_name : '—'}
						sub={cashLeader ? <>{percent(cashLeader.group_percent.cash)} {t('chain_pay_in_cash')} · {money(cashLeader.by_group.cash)}</> : null} />
				</div>
			</div>

			<div className="card chain-card mb-3">
				<div className="card-body">
					<div className="chain-toolbar">
						<div className="chain-toolbar-group">
							<h5 className="mb-0 me-2">{t('chain_payments_matrix')}</h5>
							<FilterSegment
								options={CELL_MODES.map(id => ({ id, label: modeLabels[id] }))}
								value={cellMode}
								onChange={setCellMode}
							/>
						</div>
						<HeatLegend mode="diverging" low={t('chain_more_cash')} high={t('chain_more_cashless')} />
					</div>

					<div className="chain-scroll">
						<table className="chain-table">
							<thead>
								<tr>
									<th className="chain-index chain-sticky">#</th>
									<SortTh sortKey="name" sort={sort} onSort={toggle} className="chain-sticky-2">{t('payment_type')}</SortTh>
									{branches.map((branch, index) => (
										<SortTh key={branch.pos_id} sortKey={`pos:${branch.pos_id}`} sort={sort} onSort={toggle} className="text-end">
											<span className="chain-dot" style={{ background: chainColor(index) }}></span>
											{branch.pos_name}
										</SortTh>
									))}
									<SortTh sortKey="amount" sort={sort} onSort={toggle} className="text-end chain-total-col">{t('total')}</SortTh>
									<SortTh sortKey="share" sort={sort} onSort={toggle} className="text-end">{t('share')}</SortTh>
								</tr>
							</thead>
							<tbody>
								{rows.length === 0 && <ChainEmpty colSpan={branches.length + 4} />}
								{rows.map((type, rowIndex) => {
									const values = branches.map(branch => cellValue(type, branch.pos_id))
									const rowMax = Math.max(0, ...values.map(value => Number(value || 0)))
									return (
										<tr key={type.payment_type_id}>
											<td className="chain-index chain-sticky">{rowIndex + 1}</td>
											<td className="chain-name chain-sticky-2 chain-click" onClick={() => openDetails(null, type)} title={t('chain_open_details')}>
												<span className="d-inline-flex align-items-center">
													<span className="chain-dot" style={{ background: GROUP_COLORS[type.group] }}></span>
													{typeName(type)}
												</span>
												<div className="chain-muted">{groupLabels[type.group]}</div>
											</td>
											{branches.map((branch, index) => {
												const value = values[index]
												const has = Number(type.counts?.[branch.pos_id] || 0) > 0
												return (
													<td key={branch.pos_id}
														className={`chain-num ${has ? 'chain-click' : ''}`}
														style={intensityStyle(value, rowMax, 'primary')}
														title={has ? `${branch.pos_name}: ${money(type.poses[branch.pos_id])} · ${formatMoney(type.counts[branch.pos_id])} ${t('chain_cheques_lower')}` : undefined}
														onClick={() => has && openDetails(branch.pos_id, type)}>
														<span>{formatCell(value)}</span>
													</td>
												)
											})}
											<td className="chain-num chain-total-col">
												{cellMode === 'cheques' ? formatMoney(type.cheques) : money(type.amount)}
											</td>
											<FillCell percent={type.share} tone="primary">{percent(type.share)}</FillCell>
										</tr>
									)
								})}
							</tbody>
							{rows.length > 0 && (
								<tfoot>
									<tr>
										<td className="chain-sticky"></td>
										<td className="chain-sticky-2">{t('chain_total_payments')}</td>
										{branches.map(branch => (
											<td key={branch.pos_id} className="chain-num chain-click" style={heatStyle(branch.amount, amountStats)}
												onClick={() => branch.amount > 0 && openDetails(branch.pos_id, null)}>
												{cellMode === 'cheques' ? formatMoney(branch.cheques) : money(branch.amount)}
											</td>
										))}
										<td className="chain-num chain-total-col">{cellMode === 'cheques' ? formatMoney(totals.cheques) : money(totals.amount)}</td>
										<td className="chain-num">100%</td>
									</tr>
									{hasReturns && (
										<>
											<tr>
												<td className="chain-sticky"></td>
												<td className="chain-sticky-2" title={t('chain_pay_returns_hint')}>{t('chain_returns')}</td>
												{branches.map(branch => (
													<td key={branch.pos_id} className={`chain-num ${branch.returns > 0 ? 'chain-click' : ''}`} style={{ color: branch.returns > 0 ? '#f06548' : undefined }}
														onClick={() => branch.returns > 0 && openDetails(branch.pos_id, null, 'returns')}>
														{branch.returns > 0 ? `−${money(branch.returns)}` : '—'}
													</td>
												))}
												<td className="chain-num chain-total-col" style={{ color: '#f06548' }}>−{money(totals.returns)}</td>
												<td></td>
											</tr>
											<tr>
												<td className="chain-sticky"></td>
												<td className="chain-sticky-2">{t('chain_net_revenue')}</td>
												{branches.map(branch => (
													<td key={branch.pos_id} className="chain-num">{money(branch.net)}</td>
												))}
												<td className="chain-num chain-total-col">{money(totals.net)}</td>
												<td></td>
											</tr>
										</>
									)}
									{GROUPS.filter(group => Number(totals.by_group?.[group] || 0) > 0).map(group => (
										<tr key={group}>
											<td className="chain-sticky"></td>
											<td className="chain-sticky-2">
												<span className="chain-dot" style={{ background: GROUP_COLORS[group] }}></span>
												{groupLabels[group]}, %
											</td>
											{branches.map(branch => (
												<td key={branch.pos_id} className="chain-num"
													style={group === 'other' ? undefined : heatStyle(branch.group_percent?.[group], groupStats[group], group === 'cashless')}
													title={money(branch.by_group?.[group])}>
													{percent(branch.group_percent?.[group])}
												</td>
											))}
											<td className="chain-num chain-total-col">{percent(totals.group_percent?.[group])}</td>
											<td></td>
										</tr>
									))}
								</tfoot>
							)}
						</table>
					</div>
					<div className="chain-muted mt-2">
						<i className="uil uil-info-circle me-1"></i>
						{t('chain_payments_hint')}
					</div>
				</div>
			</div>

			<StructureChart branches={branches} groupLabels={groupLabels} mode={chartMode} onMode={setChartMode} />

			{/* MODAL DETAILS */}
			<Modal show={!!details} onHide={closeDetails} size="xl" centered scrollable enforceFocus={false}>
				<Modal.Header closeButton>
					<Modal.Title>
						{details?.type_name} — {details?.pos_name}
						<div className="chain-muted fw-normal">{periodText(applied.start_date, applied.end_date)} · {formatMoney(details?.rows?.length || 0)} {details?.kind === 'returns' ? t('chain_operations_lower') : t('chain_cheques_lower')} · {money(details?.total)}</div>
					</Modal.Title>
				</Modal.Header>
				<Modal.Body style={{ height: '520px' }}>
					{details?.limited && (
						<div className="chain-note mb-2"><i className="uil uil-exclamation-triangle"></i>{t('chain_details_limited')}</div>
					)}
					<table className="chain-table">
						<thead>
							<tr>
								<th>#</th>
								<th>{t('date')}</th>
								<th>{t('chain_branch')}</th>
								<th>{t('chain_cheque_no')}</th>
								{details?.kind !== 'returns' && <th>{t('order_type')}</th>}
								<th>{t('cashier')}</th>
								<th>{details?.kind === 'returns' ? t('comment') : t('payment_types')}</th>
								<th className="text-end">{t('amount')}</th>
								<th className="text-end">{details?.kind === 'returns' ? t('payment_type') : t('chain_cheque_amount')}</th>
							</tr>
						</thead>
						<tbody>
							{(details?.rows || []).length === 0 && <ChainEmpty colSpan={9} />}
							{(details?.rows || []).map((row, index) => {
								const chequeId = details.kind === 'returns' ? row.cheque_id : row.id
								return (
									<tr key={`${row.id}-${index}`} className={`chain-click ${showRightBar && selectedCheque.id === chequeId ? 'table-tr-active' : ''}`}
										onClick={() => openCheque(chequeId)} title={t('chain_open_cheque')}>
										<td className="chain-index">{index + 1}</td>
										<td className="text-nowrap">{formatDate(row.date, 'dd.MM.yy HH:mm')}</td>
										<td>{row.pos_name}</td>
										<td>{row.cheque_number || '—'}</td>
										{details.kind !== 'returns' && <td>{orderTypeName(row.order_type)}</td>}
										<td>{row.cashier_name || '—'}</td>
										<td style={{ maxWidth: '260px' }}>{details.kind === 'returns' ? (row.comment || '—') : (row.payment_types || t('chain_payment_unknown'))}</td>
										<td className="chain-num">
											<b>{money(row.amount)}</b>
											{row.raw_amount > row.amount + 0.5 && <div className="chain-muted" title={t('chain_overpaid_row_hint')}>{t('chain_paid_in')} {money(row.raw_amount)}</div>}
										</td>
										<td className="chain-num">{details.kind === 'returns' ? row.payment_type_name : money(row.cheque_amount)}</td>
									</tr>
								)
							})}
						</tbody>
					</table>
				</Modal.Body>
				<Modal.Footer>
					<ExcelButton onClick={exportDetails} disabled={!details?.rows?.length} />
					<button type="button" className="btn btn-outline-secondary" onClick={closeDetails}>{t('close')}</button>
				</Modal.Footer>
			</Modal>

			<Rightbar
				showRightBar={showRightBar}
				setShowRightBar={setShowRightBar}
				selectedItem={selectedCheque}>
			</Rightbar>
		</>
	)
}

// Структура оплат филиалов: наличные / безнал / долг / прочее — суммой или долей
function StructureChart({ branches, groupLabels, mode, onMode }) {
	const { t } = useTranslation()
	const rows = [...branches].sort((a, b) => b.amount - a.amount)
	if (rows.length === 0) return null

	const groups = GROUPS.filter(group => rows.some(branch => Number(branch.by_group?.[group] || 0) > 0))
	const series = groups.map(group => ({
		name: groupLabels[group],
		data: rows.map(branch => Math.round(Number(branch.by_group?.[group] || 0))),
	}))

	const options = {
		chart: { type: 'bar', stacked: true, stackType: mode === 'percent' ? '100%' : undefined, toolbar: { show: false } },
		plotOptions: { bar: { horizontal: true, borderRadius: 2, barHeight: '62%' } },
		colors: groups.map(group => GROUP_COLORS[group]),
		dataLabels: { enabled: false },
		legend: { position: 'bottom', fontSize: '12px' },
		grid: { borderColor: '#eef2f6', strokeDashArray: 3 },
		xaxis: {
			categories: rows.map(branch => branch.pos_name),
			labels: { formatter: value => mode === 'percent' ? `${Math.round(value)}%` : formatMoney(Math.round(value / 1000)) + 'k' },
		},
		tooltip: { y: { formatter: value => formatMoney(Math.round(value)) } },
	}

	return (
		<div className="card chain-card mb-3">
			<div className="card-body">
				<div className="chain-card-title">
					<h5>{t('chain_payments_structure')}</h5>
					<FilterSegment
						options={[{ id: 'amount', label: t('chain_mode_amount') }, { id: 'percent', label: '%' }]}
						value={mode}
						onChange={onMode}
					/>
				</div>
				<ReactApexChart key={mode} type="bar" height={Math.max(300, rows.length * 40 + 90)} series={series} options={options} />
			</div>
		</div>
	)
}

export default Payments
